import React, { ReactNode, useState, useEffect } from "react"
import Components from "../index"
import { unified } from "unified"
import remarkParse from "remark-parse"
import remarkRehype from "remark-rehype"
import rehypeReact from "rehype-react"
import remarkReactComponents from "../../helpers/remarkReactComponents"
import { blokType, sbAssetType } from "../../types"
import linkHandler from "../../helpers/storyblokLinkHandler"
import {
  StyledCard,
  StyledCardLink,
  StyledCardImage,
  StyledCardLogo,
  StyledCardInner,
  StyledCardContent,
  StyledTitleWrapper,
  StyledCardLabel,
  StyledCardTitle,
  StyledCardDescription,
  StyledCardList,
  StyledCardForm,
  StyledCardButtons,
  StyledProgramHeader,
  StyledProgramHeading,
  StyledCardAttribute,
} from "./partials"

export interface CardProps {
  card_image: blokType[]
  card_logo: blokType[]
  label: string
  title: string
  description: string
  list: blokType[]
  form: blokType[]
  card_link: blokType[]
  buttons: blokType[]
  background_image: sbAssetType
  background_image_asset: blokType[]
  button_position: string
  background_color: string
  border_accent: string
  onClick: any
  program_category: string
  header_image: sbAssetType
  heading: string
  attribute: blokType[]
  attribute_color: string
  hover: boolean
  shadow: boolean
}

export const CardCmp: React.FC<CardProps> = ({
  card_image,
  card_logo,
  label,
  title,
  description,
  list,
  form,
  card_link,
  buttons,
  background_image,
  background_image_asset,
  button_position,
  background_color,
  border_accent,
  onClick,
  program_category,
  header_image,
  heading,
  attribute,
  attribute_color,
  hover,
  shadow,
}) => {


  return (
    <>
      <StyledCard
        data-name="card"
        linked={card_link && card_link.length > 0 ? true : false}
        hover={hover}
        onClick={onClick}
        border_accent={border_accent}
        background_image={background_image && background_image.filename}
        background_image_asset={
          background_image_asset &&
          background_image_asset.length > 0 &&
          background_image_asset[0].media.filename
        }
        background_image_asset_mobile={
          background_image_asset &&
          background_image_asset.length > 0 &&
          background_image_asset[0].mobile_image.filename
        }
        shadow={shadow}
      >
        {card_link && !title && (
          <>
            {card_link.length > 0 && (
              <StyledCardLink>
                {card_link.map((l) =>
                  React.createElement(Components(l.component), {
                    key: l._uid,
                    blok: l,
                  })
                )}
              </StyledCardLink>
            )}
          </>
        )}
        {(heading || header_image || program_category) && (
          <StyledProgramHeader
            program_category={program_category}
            header_image={header_image ? header_image.filename : ""}
          >
            <StyledProgramHeading>{heading}</StyledProgramHeading>
          </StyledProgramHeader>
        )}
        {card_image && card_image.length > 0 && (
          <StyledCardImage>
            {card_image.map((content) =>
              React.createElement(Components(content.component), {
                key: content._uid,
                blok: content,
              })
            )}
          </StyledCardImage>
        )}
        <StyledCardInner background_color={background_color} shadow={shadow}>
          {(card_logo && card_logo.length > 0) ||
          label ||
          title ||
          description ||
          (list && list.length > 0) ||
          (form && form.length > 0) ||
          (buttons && buttons.length > 0) ? (
            <StyledCardContent background_color={background_color}>
              <div>
                {card_logo && card_logo.length > 0 && (
                  <StyledCardLogo>
                    {card_logo.map((content) =>
                      React.createElement(Components(content.component), {
                        key: content._uid,
                        blok: content,
                      })
                    )}
                  </StyledCardLogo>
                )}
                <StyledTitleWrapper>
                  {label && (
                    <StyledCardLabel background_color={background_color}>
                      {label}
                    </StyledCardLabel>
                  )}
                  {title && (
                    <StyledCardTitle background_color={background_color}>
                      {card_link && card_link.length > 0 ? (
                        <a
                          href={linkHandler(card_link[0].link)}
                          title={card_link[0].title}
                          target={card_link[0].target}
                        >
                          {title}
                        </a>
                      ) : (
                        title
                      )}
                    </StyledCardTitle>
                  )}
                </StyledTitleWrapper>
                {description && (
                  <StyledCardDescription
                    background_color={background_color}
                    is_alone={label || title ? false : true}
                  >
                    {
                      unified()
                        .use(remarkParse)
                        .use(remarkRehype)
                        .use(rehypeReact as any, {
                          createElement: React.createElement,
                          components: remarkReactComponents,
                          Fragment: React.Fragment
                        })
                        .processSync(description.replace(/\n/g, "  \n"))
                        .result as ReactNode
                    }
                  </StyledCardDescription>
                )}
                {list && list.length > 0 && (
                  <StyledCardList
                    is_alone={label || title || description ? false : true}
                  >
                    {list.map((content: blokType) =>
                      React.createElement(Components(content.component), {
                        key: content._uid,
                        blok: content,
                      })
                    )}
                  </StyledCardList>
                )}
                {form && form.length > 0 && (
                  <StyledCardForm
                    is_alone={label || title || description ? false : true}
                  >
                    {form.map((content: blokType) =>
                      React.createElement(Components(content.component), {
                        key: content._uid,
                        blok: content,
                        isDark: background_color === "charcoal" ? true : false,
                      })
                    )}
                  </StyledCardForm>
                )}
                {attribute && attribute.length > 0 && (
                  <StyledCardAttribute
                    program_category={program_category}
                    attribute_color={attribute_color}
                  >
                    {attribute.map((content: blokType) =>
                      React.createElement(Components(content.component), {
                        key: content._uid,
                        blok: content,
                      })
                    )}
                  </StyledCardAttribute>
                )}
              </div>
              <div>
                {buttons && buttons.length > 0 && (
                  <StyledCardButtons
                    button_position={button_position}
                    single_button={buttons.length === 1}
                    is_alone={label || title || description ? false : true}
                    background_color={background_color}
                  >
                    {buttons.map((content: blokType) =>
                      React.createElement(Components(content.component), {
                        key: content._uid,
                        blok: content,
                      })
                    )}
                  </StyledCardButtons>
                )}
              </div>
            </StyledCardContent>
          ) : (
            <></>
          )}
        </StyledCardInner>
      </StyledCard>
    </>
  )
}
